import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { api } from '../lib/api'
import { currentEmail } from '../lib/session'
import { Msg, useI18n, useT } from '../lib/i18n'
import { ErrorBanner, Footer } from '../components/ui'

interface Country { id: string; name: string; iso2: string }
interface Language { code: string; name: string; native_name: string }

/**
 * First run. Three fields, then out.
 *
 * The language choice is applied to the interface the moment it is picked
 * rather than on submit, so the rest of this form is already in the person's
 * own language before they finish it.
 */
export default function Onboarding({ onDone }: { onDone: () => void }) {
  const msg = useT()
  const { locale, setLocale } = useI18n()
  const email = currentEmail()

  const [name, setName] = useState('')
  const [phone, setPhone] = useState('')
  const [country, setCountry] = useState('')
  const [busy, setBusy] = useState(false)
  const [problem, setProblem] = useState<unknown>(null)

  // Plain arrays -- reference data, no pagination envelope.
  const countries = useQuery<Country[]>({
    queryKey: ['countries'], queryFn: () => api('/countries'),
  })
  const languages = useQuery<Language[]>({
    queryKey: ['languages'], queryFn: () => api('/languages'),
  })

  async function submit() {
    setProblem(null)
    setBusy(true)
    try {
      await api('/me/onboarding', {
        method: 'POST',
        body: {
          full_name: name.trim(),
          phone: phone.trim() || null,
          country_id: country || null,
          locale,
        },
      })
      onDone()
    } catch (err) {
      setProblem(err)
      setBusy(false)
    }
  }

  return (
    <div className="flex h-full flex-col">
      <div className="scroller px-5"
           style={{ paddingTop: 'calc(40px + env(safe-area-inset-top,0px))' }}>
        <h1 className="font-display text-[28px] font-extrabold leading-tight">
          {msg('onboarding.finishSettingUp')}
        </h1>
        <p className="mt-2 text-[15px] leading-relaxed text-ink-3">
          {email
            ? <Msg k="onboarding.signedInAs"
                   nodes={{ email: <strong className="text-ink-2">{email}</strong> }} />
            : msg('onboarding.aFewDetails')}
        </p>

        <form className="mt-7 flex flex-col gap-5" onSubmit={(e) => { e.preventDefault(); void submit() }}>
          <div>
            <label className="label mb-2 block" htmlFor="name">{msg('onboarding.yourName')}</label>
            <input id="name" className="input" value={name} required
                   autoComplete="name" enterKeyHint="next"
                   onChange={(e) => setName(e.target.value)} />
          </div>

          <div>
            <label className="label mb-2 block" htmlFor="phone">{msg('onboarding.phoneOptional')}</label>
            <input id="phone" className="input" value={phone}
                   inputMode="tel" autoComplete="tel" enterKeyHint="next"
                   onChange={(e) => setPhone(e.target.value)} />
          </div>

          <div>
            <label className="label mb-2 block" htmlFor="country">{msg('onboarding.country')}</label>
            <select id="country" className="input" value={country}
                    onChange={(e) => setCountry(e.target.value)}>
              <option value="">{msg('onboarding.chooseACountry')}</option>
              {countries.data?.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="label mb-2 block" htmlFor="lang">{msg('language.label')}</label>
            <select id="lang" className="input" value={locale}
                    onChange={(e) => setLocale(e.target.value)}>
              {languages.data?.map((l) => (
                <option key={l.code} value={l.code}>{l.native_name}</option>
              ))}
            </select>
          </div>

          {problem !== null && <div className="-mx-4"><ErrorBanner error={problem} /></div>}
        </form>
      </div>

      <Footer>
        <button type="button" className="btn-primary" disabled={busy || !name.trim()}
                onClick={() => void submit()}>
          {busy ? msg('common.saving') : msg('common.continue')}
        </button>
      </Footer>
    </div>
  )
}
